import React from 'react';
import { AddLightMode as AddIcon } from 'src/assets/images';
import { ShowIf } from '../../../../components';
import { AddressItem } from './address-item';
import {
  Application,
  ApplicationAction,
  ApplicationActionType,
  ApplicationStep,
  Catalogue,
  Solution,
} from 'src/features/common';
import './address-summary-card.scss';
import { AddressSummaryAddressItem } from '../address-summary-address-item/address-summary-address-item';
import { DeliveryDetails } from '../../types/delivery-details';
import { yourDetailsRoute } from '../../helpers/routes';

export type Address = {
  heading: string;
  sideNote?: string;
  addressLines: string[];
  contact: {
    name: string;
    id: string;
    cellNumber: string;
    workNumber?: string;
  };
  tradingName?: string;
  productDetails: {
    devices: string;
    quantity: number;
  };
  productsDescription: string;
  isEditButtonEnabled: boolean;
  solution: Solution;
  onEditButtonClicked: () => void;
  isDeleteButtonEnabled: boolean;
  onDeleteButtonClicked: () => void;
};

export type AddressSummaryCardProps = {
  onAddAddressButtonClicked: () => void;
  isAddAddressButtonEnabled: boolean;
  addresses: Address[];
  className?: string;
  automationid?: string;
  isBrokerLogin: boolean;
  deliveryDetails: DeliveryDetails;
  catalogue: Catalogue;
  application: Application;
  dispatch: React.Dispatch<ApplicationAction>;
};

export const AddressSummaryCard = ({ className, ...props }: AddressSummaryCardProps) => {
  const totalDevices = props.addresses.reduce(
    (total, address) => total + address.productDetails.quantity,
    0,
  );

  return (
    <article className={`address-summary-card ${className}`}>
      {!props.isBrokerLogin && (
        <article className="content-box">
          <section className="full-row">
            <div className="heading-container">
              <p className="main-address-heading">Delivery Address</p>
            </div>
          </section>
          <p className="full-row">
            {totalDevices > 1 && "You've chosen more than 1 device. "}
            Devices are shipped within 5 business days on application approval and can be delivered
            to multiple addresses.
          </p>
        </article>
      )}

      {props.isBrokerLogin && (
        <AddressSummaryAddressItem
          catalogue={props.catalogue}
          application={props.application}
          onAddNewAddress={props.onAddAddressButtonClicked}
          onChangeAddress={(address, productDetails) => {
            props.dispatch({
              type: ApplicationActionType.HIGHLIGHT_ADDRESS,
              highlightedAddress: `${address}-${productDetails.quantity}-${productDetails.devices}`,
            });
            props.dispatch({ type: ApplicationActionType.NAVIGATE_TO_PREVIOUS_STEP });
          }}
          onAddressEdit={() => {
            const yourDetailsStep: ApplicationStep | undefined = props.application.steps?.find(
              (step) => step.route === yourDetailsRoute,
            );

            if (!yourDetailsStep) {
              throw new Error(
                `Address summary revamp: step name not supported: ${yourDetailsRoute} not found`,
              );
            }

            props.dispatch({
              type: ApplicationActionType.QUICK_EDIT,
              steps: [yourDetailsStep],
            });
          }}
          deliveryDetails={props.deliveryDetails}
        />
      )}

      {!props.isBrokerLogin &&
        props.addresses.map((address, index) => (
          <React.Fragment key={index}>
            <AddressItem key={index} address={address} automationid={props.automationid} />
            {index !== 0 && !props.isBrokerLogin && <hr />}
          </React.Fragment>
        ))}

      <ShowIf condition={props.isAddAddressButtonEnabled && !props.isBrokerLogin}>
        <section onClick={props.onAddAddressButtonClicked} className="add-line">
          <img
            src={AddIcon}
            alt="+"
            onClick={props.onAddAddressButtonClicked}
            automationid={`${props.automationid}_Add_Address`}
          />
          <p>Add address</p>
        </section>
      </ShowIf>
    </article>
  );
};
